import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import ScriptAnalysisPanel from './ScriptAnalysisPanel';
import ChatWidget from './ChatWidget';
import './CollaborationDetail.css';

// URL을 클릭 가능한 링크로 변환하고 줄바꿈을 처리하는 함수
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a 
          key={index} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer"
          className="feedback-link"
        >
          {part}
        </a>
      );
    }
    // 줄바꿈을 <br> 태그로 변환
    return part.split('\n').map((line, lineIndex) => (
      <React.Fragment key={`${index}-${lineIndex}`}>
        {line}
        {lineIndex < part.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  });
};

// Helper function to get social media URL
const getSocialMediaUrl = (platform: string, handle: string): string => {
  const cleanHandle = handle.replace('@', '');
  switch (platform.toLowerCase()) {
    case 'instagram':
      return `https://www.instagram.com/${cleanHandle}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${cleanHandle}`;
    case 'youtube':
      return `https://www.youtube.com/@${cleanHandle}`;
    case 'twitter':
    case 'x':
      return `https://x.com/${cleanHandle}`;
    case 'facebook':
      return `https://www.facebook.com/${cleanHandle}`;
    case 'linkedin':
      return `https://www.linkedin.com/in/${cleanHandle}`;
    case 'twitch':
      return `https://www.twitch.tv/${cleanHandle}`;
    default:
      return '#';
  }
};

interface EmailHistoryItem {
  _id: string;
  email: string;
  dateSent: string;
  subject: string;
  content?: string;
  ruleName?: string;
  status: string;
  campaignId?: string;
  metadata?: any;
}

interface Memo {
  _id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

interface CollaborationDetail {
  _id: string;
  responseId: any;
  email: string;
  platform: string;
  handle: string;
  agreedPrice: number;
  collaborationStage: string;
  sourceType: string;
  paused?: boolean;
  skipScriptStage?: boolean;
  memos?: Memo[];
  scriptDraft?: string;
  scriptSubmittedAt?: string;
  scriptApproved?: boolean;
  scriptFeedback?: string;
  scriptApprovedAt?: string;
  scriptHistory?: Array<{
    type: 'submission' | 'feedback';
    version?: number;
    content: string;
    timestamp: string;
    approved?: boolean;
    author: 'Creator' | 'Admin';
  }>;
  videoFile?: string;
  videoSubmittedAt?: string;
  videoApproved?: boolean;
  videoFeedback?: string;
  videoApprovedAt?: string;
  videoHistory?: Array<{
    type: 'submission' | 'feedback';
    version?: number;
    content: string;
    timestamp: string;
    approved?: boolean;
    author: 'Creator' | 'Admin';
  }>;
  postUrlInstagram?: string;
  postUrlTikTok?: string;
  postUrlYoutube?: string;
  sparkAdsCode?: string;
  postHistory?: Array<{
    type: 'submission' | 'feedback';
    version?: number;
    content: string;
    timestamp: string;
    approved?: boolean;
    author: 'Creator' | 'Admin';
  }>;
  postSubmittedAt?: string;
  postVerified?: boolean;
  postVerifiedAt?: string;
  postValidation?: {
    isValid: boolean;
    transcript: string;
    similarityScore: number;
    analysisMethod: string;
    analysisDetails: string;
    keyDifferences: string[];
    timestamp: string;
    videoUrl: string;
    verdict: string;
  };

  paymentAmount?: number;
  paymentDate?: string;
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    country?: string;
    address?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    submittedAt?: string;
  };
  invoiceLink?: string;
  createdAt: string;
  updatedAt: string;
}

const CollaborationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [collaboration, setCollaboration] = useState<CollaborationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'script' | 'video' | 'post' | 'payment' | 'emails'>('script');
  const [emailHistory, setEmailHistory] = useState<EmailHistoryItem[]>([]);
  const [feedback, setFeedback] = useState('');
  const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingStage, setEditingStage] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoContent, setMemoContent] = useState('');
  const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
  const [submittingMemo, setSubmittingMemo] = useState(false);


  // Helper function to format dates correctly
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchCollaboration();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 결제 상태가 processing_payment인 경우 자동으로 상태 확인
  useEffect(() => {
    if (collaboration?.collaborationStage === 'processing_payment') {
      refreshPaymentStatus();
      setAutoRefresh(true);
    } else {
      setAutoRefresh(false);
    }
  }, [collaboration?.collaborationStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // 자동 새로고침 (processing_payment 상태일 때만)
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (autoRefresh && collaboration?.collaborationStage === 'processing_payment') {
      console.log('🔄 Starting auto-refresh for payment status');
      interval = setInterval(() => {
        refreshPaymentStatus(true); // silent refresh
      }, 5000); // 5초마다 확인
    }
    
    return () => {
      if (interval) {
        console.log('⏹️ Stopping auto-refresh');
        clearInterval(interval);
      }
    };
  }, [autoRefresh, collaboration?.collaborationStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // 컴포넌트 언마운트 시 자동 새로고침 정리
  useEffect(() => {
    return () => {
      setAutoRefresh(false);
    };
  }, []);

  const fetchCollaboration = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/collaborations/${id}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success) {
        setCollaboration(data.data);

        // Set email history if available
        if (data.data.emailHistory) {
          setEmailHistory(data.data.emailHistory);
        }

        // Set active tab based on current stage
        const stage = data.data.collaborationStage;
        if (stage.includes('script')) setActiveTab('script');
        else if (stage.includes('video')) setActiveTab('video');
        else if (stage.includes('post')) setActiveTab('post');
        else if (stage.includes('payment') || stage === 'completed') setActiveTab('payment');
      }
    } catch (error) {
      console.error('Error fetching collaboration:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);


  const handleApprove = async (type: 'script' | 'video') => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/collaborations/${id}/${type}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          approved: true
          // 승인 시에는 feedback을 전송하지 않음
        }),
      });

      if (response.ok) {
        await fetchCollaboration();
        setFeedback('');
        setFeedbackFiles([]);
      }
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (type: 'script' | 'video') => {
    if (!feedback) {
      alert('Please provide feedback for the creator');
      return;
    }

    try {
      setSubmitting(true);

      // Always use JSON with uploadedFiles if available
      const response = await fetch(`${API_URL}/api/collaborations/${id}/${type}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          approved: false,
          feedback: feedback,
          ...(uploadedFiles.length > 0 && { uploadedFiles: uploadedFiles })
        }),
      });

      if (response.ok) {
        await fetchCollaboration();
        setFeedback('');
        setUploadedFiles([]);
        setFeedbackFiles([]);
      } else {
        // Handle HTTP error responses
        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
        console.error('Server error:', errorData);
        alert(`Failed to submit feedback: ${errorData.error || 'Server error'}`);
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Network error: Failed to submit feedback. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStageChange = async () => {
    if (!selectedStage || selectedStage === collaboration?.collaborationStage) {
      setEditingStage(false);
      return;
    }

    const confirmChange = window.confirm(
      `Are you sure you want to change the stage from "${getStageInfo(collaboration!.collaborationStage).label}" to "${getStageInfo(selectedStage).label}"?`
    );

    if (!confirmChange) {
      setEditingStage(false);
      setSelectedStage('');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/collaborations/${id}/admin/stage`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          stage: selectedStage
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchCollaboration();
        setEditingStage(false);
        setSelectedStage('');
      } else {
        alert(data.error || 'Failed to update stage');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Failed to update collaboration stage');
    } finally {
      setSubmitting(false);
    }
  };

  const getStageInfo = (stage: string) => {
    const stages: { [key: string]: any } = {
      'scheduled': { label: 'Scheduled', color: '#9e9e9e', icon: '📅' },
      'awaiting_script': { label: 'Awaiting Script', color: '#ffc107', icon: '📝' },
      'reviewing_script': { label: 'Script Review', color: '#ff9800', icon: '📄' },
      'awaiting_video': { label: 'Awaiting Video', color: '#17a2b8', icon: '🎥' },
      'reviewing_video': { label: 'Video Review', color: '#fd7e14', icon: '🎬' },
      'awaiting_post': { label: 'Awaiting Post', color: '#007bff', icon: '📱' },
      'reviewing_post': { label: 'Post Review', color: '#6610f2', icon: '🔍' },
      'awaiting_payment_info': { label: 'Awaiting Payment Info', color: '#6f42c1', icon: '💳' },
      'processing_payment': { label: 'Processing Payment', color: '#20c997', icon: '⏳' },
      'completed': { label: 'Completed', color: '#28a745', icon: '✅' }
    };
    return stages[stage] || { label: stage, color: '#6c757d', icon: '📋' };
  };

  const getAllStages = () => {
    return [
      'scheduled',
      'awaiting_script',
      'reviewing_script',
      'awaiting_video',
      'reviewing_video',
      'awaiting_post',
      'reviewing_post',
      'awaiting_payment_info',
      'processing_payment',
      'completed'
    ];
  };



  const getStageColor = (stage: string) => {
    const colors: { [key: string]: string } = {
      'scheduled': '#9e9e9e',
      'awaiting_script': '#ffc107',
      'reviewing_script': '#ff9800', 
      'awaiting_video': '#17a2b8',
      'reviewing_video': '#6610f2',
      'awaiting_post': '#fd7e14',
      'reviewing_post': '#e83e8c',
      'awaiting_payment_info': '#20c997',
      'processing_payment': '#007bff',
      'completed': '#28a745'
    };
    return colors[stage] || '#6c757d';
  };

  const getStageDisplay = (stage: string) => {
    const displays: { [key: string]: string } = {
      'scheduled': '📅 Scheduled',
      'awaiting_script': '📝 Awaiting Script',
      'reviewing_script': '🔍 Reviewing Script',
      'awaiting_video': '🎥 Awaiting Video', 
      'reviewing_video': '🔍 Reviewing Video',
      'awaiting_post': '📱 Awaiting Post',
      'reviewing_post': '🔍 Reviewing Post',
      'awaiting_payment_info': '💳 Awaiting Payment Info',
      'processing_payment': '⏳ Processing Payment',
      'completed': '✅ Completed'
    };
    return displays[stage] || stage;
  };

  // 결제 상태 새로고침
  const refreshPaymentStatus = useCallback(async (silent = false) => {
    if (!collaboration) return;
    
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/payment-status?refresh=true`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const newStatus = data.data;
        
        // 상태가 변경되었으면 collaboration 정보도 업데이트
        if (newStatus.collaborationStage !== collaboration.collaborationStage) {
          console.log(`🔄 Status changed: ${collaboration.collaborationStage} → ${newStatus.collaborationStage}`);
          await fetchCollaboration();
          
          // 완료되면 자동 새로고침 중단
          if (newStatus.collaborationStage === 'completed') {
            setAutoRefresh(false);
            if (!silent) {
              alert('🎉 Payment completed successfully!');
            }
          }
        }
        
      }
    } catch (error) {
      console.error('Error refreshing payment status:', error);
      if (!silent) {
        alert('Failed to refresh payment status');
      }
    } finally {
    }
  }, [collaboration, fetchCollaboration, token]);

  // 메모 관련 함수들
  const handleAddMemo = async () => {
    if (!memoContent.trim()) {
      alert('메모 내용을 입력해주세요.');
      return;
    }

    setSubmittingMemo(true);
    try {
      const url = editingMemoId
        ? `${API_URL}/api/collaborations/${collaboration?._id}/memos/${editingMemoId}`
        : `${API_URL}/api/collaborations/${collaboration?._id}/memos`;

      const method = editingMemoId ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ content: memoContent }),
      });

      if (response.ok) {
        await fetchCollaboration();
        setMemoContent('');
        setEditingMemoId(null);
        setShowMemoModal(false);
      } else {
        alert('메모 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving memo:', error);
      alert('메모 저장 중 오류가 발생했습니다.');
    } finally {
      setSubmittingMemo(false);
    }
  };

  const handleEditMemo = (memo: Memo) => {
    setEditingMemoId(memo._id);
    setMemoContent(memo.content);
    setShowMemoModal(true);
  };

  const handleDeleteMemo = async (memoId: string) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collaboration?._id}/memos/${memoId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        await fetchCollaboration();
      } else {
        alert('메모 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error deleting memo:', error);
      alert('메모 삭제 중 오류가 발생했습니다.');
    }
  };

  // 수동 결제 처리
  const markAsPaid = async () => {
    if (!collaboration) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to mark this payment as completed?\n\nCreator: ${collaboration.handle}\nAmount: $${collaboration.agreedPrice}\n\nPlease ensure the PayPal invoice has been paid.`
    );
    
    if (!confirmed) return;
    
    setProcessingPayment(true);
    setPaymentError(null);
    setErrorDetails(null);
    
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/mark-as-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          paymentAmount: collaboration.agreedPrice,
          paymentMethod: 'PayPal',
          paymentDate: new Date().toISOString()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Payment marked as completed successfully!');
        setPaymentError(null);
        setErrorDetails(null);
        await fetchCollaboration();

        // Automatically fetch metrics for completed collaboration
        try {
          console.log('🔄 Auto-fetching metrics for completed collaboration...');
          const metricsResponse = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/fetch-metrics`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          });

          const metricsData = await metricsResponse.json();
          if (metricsData.success) {
            console.log('✅ Metrics fetched successfully:', metricsData.data);
            // Refresh collaboration data to show updated metrics
            await fetchCollaboration();
          } else {
            console.warn('⚠️ Failed to fetch metrics:', metricsData.error);
          }
        } catch (metricsError) {
          console.error('❌ Error auto-fetching metrics:', metricsError);
          // Don't show error to user, just log it
        }
      } else {
        const errorMsg = `Payment failed: ${data.error}`;
        setPaymentError(errorMsg);
        setErrorDetails({
          error: data.error,
          details: data.details,
          suggestion: data.suggestion,
          timestamp: new Date().toISOString()
        });
        
        // Show detailed error in alert
        alert(`❌ ${errorMsg}\n\n${data.details || ''}\n\n💡 ${data.suggestion || ''}`);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      const errorMsg = 'Network error: Failed to process payment';
      setPaymentError(errorMsg);
      setErrorDetails({
        error: errorMsg,
        details: error instanceof Error ? error.message : 'Unknown error occurred',
        suggestion: 'Check your internet connection and try again',
        timestamp: new Date().toISOString()
      });
      alert('❌ Network error. Please check your connection and try again.');
    } finally {
      setProcessingPayment(false);
    }
  };




  if (loading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading collaboration details...</p>
      </div>
    );
  }

  if (!collaboration) {
    return (
      <div className="detail-error">
        <h2>Collaboration not found</h2>
        <button onClick={() => window.history.back()}>Back to Collaborations</button>
      </div>
    );
  }

  const stageInfo = getStageInfo(collaboration.collaborationStage);

  return (
    <div className="collaboration-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="header-actions">
          <button className="back-button" onClick={() => window.history.back()}>
            ← Back to Collaborations
          </button>
          <button 
            className="creator-view-button"
            onClick={() => window.open(`/collaborations-creator/${collaboration._id}`, '_blank')}
            title="View as Creator"
          >
            👁️ View Creator Page
          </button>
          <button
            className="memo-button"
            onClick={() => {
              setEditingMemoId(null);
              setMemoContent('');
              setShowMemoModal(true);
            }}
            style={{
              background: '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              marginRight: '8px'
            }}
            title="Add or view memos"
          >
            📝 Memo {collaboration.memos && collaboration.memos.length > 0 && `(${collaboration.memos.length})`}
          </button>
          <button
            className="skip-script-toggle-button"
            onClick={async () => {
              const newValue = !collaboration.skipScriptStage;
              try {
                const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/skip-script-stage`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                  body: JSON.stringify({ skipScriptStage: newValue })
                });
                if (response.ok) {
                  await fetchCollaboration();
                  alert(`Skip Script Stage ${newValue ? 'enabled' : 'disabled'} successfully`);
                } else {
                  alert('Failed to update skip script stage');
                }
              } catch (error) {
                alert('Error updating skip script stage');
              }
            }}
            style={{
              background: collaboration.skipScriptStage ? '#10b981' : '#6b7280',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              marginRight: '8px'
            }}
            title={collaboration.skipScriptStage ? 'Script stage will be skipped' : 'Script stage is required'}
          >
            {collaboration.skipScriptStage ? '⏭️ Skip Script: ON' : '📝 Skip Script: OFF'}
          </button>
          <button
            className="pause-toggle-button"
            onClick={async () => {
              const action = collaboration.paused ? 'unpause' : 'pause';
              if (window.confirm(`Are you sure you want to ${action} this collaboration?`)) {
                try {
                  const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/admin/pause`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                    body: JSON.stringify({ paused: !collaboration.paused })
                  });
                  if (response.ok) {
                    await fetchCollaboration();
                    alert(`Collaboration ${action}d successfully`);
                  } else {
                    alert(`Failed to ${action} collaboration`);
                  }
                } catch (error) {
                  alert(`Error ${action}ing collaboration`);
                }
              }
            }}
            style={{
              background: collaboration.paused ? '#10b981' : '#ef4444',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
            title={collaboration.paused ? 'Unpause collaboration' : 'Pause collaboration'}
          >
            {collaboration.paused ? '▶️ Unpause' : '⏸️ Pause'}
          </button>
        </div>
        <div className="header-info">
          <h1>Collaboration with {collaboration.handle}</h1>
          <div className="header-meta">
            <a 
              href={getSocialMediaUrl(collaboration.platform, collaboration.handle)}
              target="_blank"
              rel="noopener noreferrer"
              className={`platform-badge platform-${collaboration.platform.toLowerCase()}`}
              title={`View ${collaboration.handle}'s ${collaboration.platform} account`}
            >
              {collaboration.platform}
            </a>
            <span className="price-badge">💰 ${collaboration.agreedPrice}</span>
            {collaboration.paused && (
              <span 
                className="paused-indicator" 
                style={{
                  background: '#ef4444',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                ⏸️ PAUSED
              </span>
            )}
            {editingStage ? (
              <div className="stage-editor">
                <select 
                  value={selectedStage || collaboration.collaborationStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className="stage-select"
                  disabled={submitting}
                >
                  {getAllStages().map(stage => {
                    const info = getStageInfo(stage);
                    return (
                      <option key={stage} value={stage}>
                        {info.icon} {info.label}
                      </option>
                    );
                  })}
                </select>
                <button 
                  onClick={handleStageChange}
                  className="stage-save-btn"
                  disabled={submitting}
                >
                  ✓
                </button>
                <button 
                  onClick={() => {
                    setEditingStage(false);
                    setSelectedStage('');
                  }}
                  className="stage-cancel-btn"
                  disabled={submitting}
                >
                  ✕
                </button>
              </div>
            ) : (
              <span 
                className="stage-badge clickable" 
                style={{ background: stageInfo.color, cursor: 'pointer' }}
                onClick={() => {
                  setEditingStage(true);
                  setSelectedStage(collaboration.collaborationStage);
                }}
                title="Click to change stage"
              >
                {stageInfo.icon} {stageInfo.label} ✏️
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'script' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('script');
            setUploadedFiles([]);
            setFeedback('');
          }}
        >
          📝 Script
        </button>
        <button
          className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('video');
            setUploadedFiles([]);
            setFeedback('');
          }}
        >
          🎥 Video
        </button>
        <button
          className={`tab-button ${activeTab === 'post' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('post');
            setUploadedFiles([]);
            setFeedback('');
          }}
        >
          📱 Post
        </button>
        <button
          className={`tab-button ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          💰 Payment
        </button>
        <button
          className={`tab-button ${activeTab === 'emails' ? 'active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          📧 Email History
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'script' && (
          <div className="script-section">
            <h2>Script Timeline</h2>
            
            {/* Script Timeline */}
            {collaboration.scriptHistory && collaboration.scriptHistory.length > 0 ? (
              <div className="admin-timeline">
                {collaboration.scriptHistory
                  .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                  .map((item, index) => (
                    <div key={index} className="timeline-row">
                      <div className="timeline-icon">
                        {item.type === 'submission' ? '📄' : '💬'}
                      </div>
                      <div className="timeline-content">
                        {item.type === 'submission' ? (
                          <>
                            <div className="timeline-header">
                              <span className="timeline-label">Script v{item.version}</span>
                              <span className="timeline-time">{formatDate(item.timestamp)}</span>
                            </div>
                            <div className="timeline-text">{item.content}</div>
                          </>
                        ) : (
                          <>
                            <div className="timeline-header">
                              <span className={`timeline-label ${item.author === 'Admin' ? 'admin-feedback' : item.author === 'Creator' ? 'creator-feedback' : ''}`}>
                                {item.author === 'Admin' ? '👨‍💼 Admin' : item.author === 'Creator' ? '🎨 Creator' : 'Feedback'}
                              </span>
                              <span className="timeline-time">{formatDate(item.timestamp)}</span>
                              {item.approved && <span className="timeline-approved">✅ Approved</span>}
                            </div>
                            <div className="timeline-feedback-text">
                              {linkifyText(item.content)}
                              {(item as any).attachments && (item as any).attachments.length > 0 && (
                                <div className="feedback-attachments-display">
                                  <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Attachments:</p>
                                  <div className="attachment-grid">
                                    {(item as any).attachments.map((attachment: any, attIndex: number) => (
                                      <div key={attIndex} className="attachment-item">
                                        {attachment.mimetype.startsWith('image/') ? (
                                          <img
                                            src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            alt={attachment.originalname || attachment.filename}
                                            className="attachment-image"
                                          />
                                        ) : attachment.mimetype.startsWith('video/') ? (
                                          <video
                                            src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            controls
                                            preload="metadata"
                                            className="attachment-video"
                                            style={{ 
                                              display: 'block',
                                              outline: 'none'
                                            }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        ) : (
                                          <a
                                            href={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-link"
                                          >
                                            📎 {attachment.filename}
                                          </a>
                                        )}
                                        <span className="attachment-name">{attachment.originalname || attachment.filename}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No script submissions yet</p>
              </div>
            )}

            {/* Admin Review Section - Show based on collaboration stage */}
            {(() => {
              const lastEvent = collaboration.scriptHistory && collaboration.scriptHistory.length > 0
                ? collaboration.scriptHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                : null;
              
              // Check current collaboration stage
              if (collaboration.collaborationStage === 'reviewing_script') {
                return (
                  <div className="review-section">
                    <h3>🗒️ Review & Feedback</h3>

                    {/* AI Analysis Panel */}
                    <ScriptAnalysisPanel
                      collaborationId={collaboration._id}
                      onFeedbackGenerated={(generatedFeedback) => {
                        setFeedback(generatedFeedback);
                      }}
                    />

                    <textarea
                      className="feedback-input"
                      placeholder="Provide feedback for the creator..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                    
                    {/* File Upload Section */}
                    <div className="feedback-attachments">
                      <label className="file-upload-label">
                        📎 Attach files (images/videos)
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length === 0) return;

                            setUploadingFiles(true);
                            try {
                              const formData = new FormData();
                              files.forEach(file => {
                                formData.append('files', file);
                              });

                              const response = await fetch(`${API_URL}/api/collaborations/${id}/script-review/upload`, {
                                method: 'POST',
                                headers: {
                                  ...(token && { 'Authorization': `Bearer ${token}` })
                                },
                                body: formData
                              });

                              if (response.ok) {
                                const data = await response.json();
                                // data.data contains the uploaded files array
                                setUploadedFiles(prev => [...prev, ...data.data]);
                              } else {
                                const error = await response.json();
                                alert(`Failed to upload files: ${error.error || 'Unknown error'}`);
                              }
                            } catch (error) {
                              console.error('Error uploading files:', error);
                              alert('Error uploading files');
                            } finally {
                              setUploadingFiles(false);
                              e.target.value = '';
                            }
                          }}
                          className="file-upload-input"
                        />
                      </label>

                      {/* Preview uploaded files */}
                      {uploadingFiles && <p>Uploading files...</p>}
                      {uploadedFiles.length > 0 && (
                        <div className="uploaded-files">
                          <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Uploaded Files:</p>
                          <div className="attachment-grid">
                            {uploadedFiles.map((file, index) => (
                              <div key={index} className="attachment-item">
                                {file.mimetype?.startsWith('image/') ? (
                                  <>
                                    <img
                                      src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                      alt={file.filename}
                                      className="attachment-image"
                                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                    />
                                    <span className="attachment-name">{file.filename}</span>
                                  </>
                                ) : file.mimetype?.startsWith('video/') ? (
                                  <>
                                    <video
                                      src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                      controls
                                      className="attachment-video"
                                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                                    />
                                    <span className="attachment-name">{file.filename}</span>
                                  </>
                                ) : (
                                  <>
                                    <div className="attachment-link">📎 {file.filename}</div>
                                  </>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                                  className="file-remove-btn"
                                  style={{ marginTop: '5px' }}
                                >
                                  ✕ Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="review-actions">
                      <button
                        className="btn-reject"
                        onClick={() => handleReject('script')}
                        disabled={submitting}
                      >
                        ❌ Request Changes
                      </button>
                      <button 
                        className="btn-approve"
                        onClick={() => handleApprove('script')}
                        disabled={submitting}
                      >
                        ✅ Approve Script
                      </button>
                    </div>
                  </div>
                );
              }
              
              if (collaboration.collaborationStage === 'awaiting_script') {
                if (lastEvent && lastEvent.type === 'feedback' && !lastEvent.approved) {
                  return (
                    <div className="status-box waiting">
                      <span>⏳</span> Waiting for revised script submission
                      <p className="status-description">Feedback has been sent to the creator. They will submit a revised version soon.</p>
                      
                      {/* 추가 피드백 섹션 */}
                      <div className="additional-feedback-section">
                        <h4>📝 Send Additional Feedback</h4>
                        <textarea
                          className="feedback-input"
                          placeholder="Send additional feedback to the creator..."
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                        />
                        
                        {/* File Upload Section */}
                        <div className="feedback-attachments">
                          <label className="file-upload-label">
                            📎 Attach files (images/videos)
                            <input
                              type="file"
                              multiple
                              accept="image/*,video/*"
                              onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length === 0) return;

                                setUploadingFiles(true);
                                try {
                                  const formData = new FormData();
                                  files.forEach(file => {
                                    formData.append('files', file);
                                  });

                                  const response = await fetch(`${API_URL}/api/collaborations/${id}/script-review/upload`, {
                                    method: 'POST',
                                    body: formData
                                  });

                                  if (response.ok) {
                                    const data = await response.json();
                                    setUploadedFiles(prev => [...prev, ...data.files]);
                                  } else {
                                    alert('Failed to upload files');
                                  }
                                } catch (error) {
                                  console.error('Error uploading files:', error);
                                  alert('Error uploading files');
                                } finally {
                                  setUploadingFiles(false);
                                  e.target.value = '';
                                }
                              }}
                              className="file-upload-input"
                            />
                          </label>
                          
                          {/* Preview uploaded files */}
                          {uploadingFiles && <p>Uploading files...</p>}
                          {uploadedFiles.length > 0 && (
                            <div className="uploaded-files">
                              <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Uploaded Files:</p>
                              <div className="attachment-grid">
                                {uploadedFiles.map((file, index) => (
                                  <div key={index} className="attachment-item">
                                    {file.mimetype?.startsWith('image/') ? (
                                      <>
                                        <img
                          src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                          alt={file.originalname || file.filename}
                                          className="attachment-image"
                                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                        />
                                        <span className="attachment-name">{file.originalname || file.filename}</span>
                                      </>
                                    ) : file.mimetype?.startsWith('video/') ? (
                                      <>
                                        <video
                                          src={`${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                          controls
                                          className="attachment-video"
                                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                        <span className="attachment-name">{file.originalname || file.filename}</span>
                                      </>
                                    ) : (
                                      <>
                                        <div className="attachment-link">📎 {file.originalname || file.filename}</div>
                                      </>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                                      className="file-remove-btn"
                                      style={{ marginTop: '5px' }}
                                    >
                                      ✕ Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button 
                          className="btn-additional-feedback"
                          onClick={() => handleReject('script')}
                          disabled={submitting || !feedback.trim()}
                        >
                          💬 Send Additional Feedback
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="status-box waiting">
                      <span>⏳</span> Waiting for script submission
                      <p className="status-description">The creator will submit their script draft soon.</p>
                    </div>
                  );
                }
              }
              
              // If script is approved (stage moved beyond script phases)
              if (['awaiting_video', 'reviewing_video', 'awaiting_post', 'reviewing_post', 'awaiting_payment_info', 'processing_payment', 'completed'].includes(collaboration.collaborationStage)) {
                return (
                  <div className="status-box approved">
                    <span>✅</span> Script Approved
                  </div>
                );
              }
              
              return null;
            })()}
          </div>
        )}

        {activeTab === 'video' && (
          <div className="video-section">
            <h2>Video Timeline</h2>
            
            {/* Video Timeline */}
            {collaboration.videoHistory && collaboration.videoHistory.length > 0 ? (
              <div className="admin-timeline">
                {collaboration.videoHistory
                  .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                  .map((item, index) => (
                    <div key={index} className="timeline-row">
                      <div className="timeline-icon">
                        {item.type === 'submission' ? '🎥' : '💬'}
                      </div>
                      <div className="timeline-content">
                        {item.type === 'submission' ? (
                          <>
                            <div className="timeline-header">
                              <span className="timeline-label">Video v{item.version}</span>
                              <span className="timeline-time">{formatDate(item.timestamp)}</span>
                            </div>
                            <video 
                              className="timeline-video" 
                              controls
                              preload="metadata"
                              style={{ 
                                display: 'block',
                                outline: 'none'
                              }}
                            >
                              <source src={item.content} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </>
                        ) : (
                          <>
                            <div className="timeline-header">
                              <span className={`timeline-label ${item.author === 'Admin' ? 'admin-feedback' : item.author === 'Creator' ? 'creator-feedback' : ''}`}>
                                {item.author === 'Admin' ? '👨‍💼 Admin' : item.author === 'Creator' ? '🎨 Creator' : 'Feedback'}
                              </span>
                              <span className="timeline-time">{formatDate(item.timestamp)}</span>
                              {item.approved && <span className="timeline-approved">✅ Approved</span>}
                            </div>
                            <div className="timeline-feedback-text">
                              {linkifyText(item.content)}
                              {(item as any).attachments && (item as any).attachments.length > 0 && (
                                <div className="feedback-attachments-display">
                                  <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Attachments:</p>
                                  <div className="attachment-grid">
                                    {(item as any).attachments.map((attachment: any, attIndex: number) => (
                                      <div key={attIndex} className="attachment-item">
                                        {attachment.mimetype.startsWith('image/') ? (
                                          <img
                                            src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            alt={attachment.originalname || attachment.filename}
                                            className="attachment-image"
                                          />
                                        ) : attachment.mimetype.startsWith('video/') ? (
                                          <video
                                            src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            controls
                                            preload="metadata"
                                            className="attachment-video"
                                            style={{ 
                                              display: 'block',
                                              outline: 'none'
                                            }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        ) : (
                                          <a
                                            href={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-link"
                                          >
                                            📎 {attachment.filename}
                                          </a>
                                        )}
                                        <span className="attachment-name">{attachment.originalname || attachment.filename}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No video submissions yet</p>
              </div>
            )}

            {/* Admin Review Section - Show based on collaboration stage */}
            {(() => {
              const lastEvent = collaboration.videoHistory && collaboration.videoHistory.length > 0
                ? collaboration.videoHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                : null;
              
              // Check current collaboration stage
              if (collaboration.collaborationStage === 'reviewing_video') {
                return (
                  <div className="review-section">
                    <h3>🗒️ Review & Feedback</h3>
                    <textarea
                      className="feedback-input"
                      placeholder="Provide feedback for the creator..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />

                    {/* File Upload Section */}
                    <div className="feedback-attachments">
                      <label className="file-upload-label">
                        📎 Attach files (images/videos)
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length === 0) return;

                            setUploadingFiles(true);
                            try {
                              const formData = new FormData();
                              files.forEach(file => {
                                formData.append('files', file);
                              });

                              const response = await fetch(`${API_URL}/api/collaborations/${id}/video-review/upload`, {
                                method: 'POST',
                                headers: {
                                  ...(token && { 'Authorization': `Bearer ${token}` })
                                },
                                body: formData
                              });

                              if (response.ok) {
                                const data = await response.json();
                                // data.data contains the uploaded files array
                                setUploadedFiles(prev => [...prev, ...data.data]);
                              } else {
                                const error = await response.json();
                                alert(`Failed to upload files: ${error.error || 'Unknown error'}`);
                              }
                            } catch (error) {
                              console.error('Error uploading files:', error);
                              alert('Error uploading files');
                            } finally {
                              setUploadingFiles(false);
                              e.target.value = '';
                            }
                          }}
                          className="file-upload-input"
                        />
                      </label>
                      
                      {/* Preview uploaded files */}
                      {uploadingFiles && <p>Uploading files...</p>}
                      {uploadedFiles.length > 0 && (
                        <div className="uploaded-files">
                          <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Uploaded Files:</p>
                          <div className="attachment-grid">
                            {uploadedFiles.map((file, index) => (
                              <div key={index} className="attachment-item">
                                {file.mimetype?.startsWith('image/') ? (
                                  <>
                                    <img
                                      src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                      alt={file.filename}
                                      className="attachment-image"
                                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                    />
                                    <span className="attachment-name">{file.filename}</span>
                                  </>
                                ) : file.mimetype?.startsWith('video/') ? (
                                  <>
                                    <video
                                      src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                      controls
                                      className="attachment-video"
                                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                                    />
                                    <span className="attachment-name">{file.filename}</span>
                                  </>
                                ) : (
                                  <>
                                    <div className="attachment-link">📎 {file.filename}</div>
                                  </>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                                  className="file-remove-btn"
                                  style={{ marginTop: '5px' }}
                                >
                                  ✕ Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="review-actions">
                      <button 
                        className="btn-reject"
                        onClick={() => handleReject('video')}
                        disabled={submitting}
                      >
                        ❌ Request Changes
                      </button>
                      <button 
                        className="btn-approve"
                        onClick={() => handleApprove('video')}
                        disabled={submitting}
                      >
                        ✅ Approve Video
                      </button>
                    </div>
                  </div>
                );
              }
              
              if (collaboration.collaborationStage === 'awaiting_video') {
                if (lastEvent && lastEvent.type === 'feedback' && !lastEvent.approved) {
                  return (
                    <div className="status-box waiting">
                      <span>⏳</span> Waiting for revised video submission
                      <p className="status-description">Feedback has been sent to the creator. They will submit a revised version soon.</p>
                      
                      {/* 추가 피드백 섹션 */}
                      <div className="additional-feedback-section">
                        <h4>🎥 Send Additional Feedback</h4>
                        <textarea
                          className="feedback-input"
                          placeholder="Send additional feedback to the creator..."
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                        />
                        
                        {/* File Upload Section */}
                        <div className="feedback-attachments">
                          <label className="file-upload-label">
                            📎 Attach files (images/videos)
                            <input
                              type="file"
                              multiple
                              accept="image/*,video/*"
                              onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length === 0) return;

                                setUploadingFiles(true);
                                try {
                                  const formData = new FormData();
                                  files.forEach(file => {
                                    formData.append('files', file);
                                  });

                                  const response = await fetch(`${API_URL}/api/collaborations/${id}/video-review/upload`, {
                                    method: 'POST',
                                    body: formData
                                  });

                                  if (response.ok) {
                                    const data = await response.json();
                                    setUploadedFiles(prev => [...prev, ...data.data]);
                                  } else {
                                    alert('Failed to upload files');
                                  }
                                } catch (error) {
                                  console.error('Error uploading files:', error);
                                  alert('Error uploading files');
                                } finally {
                                  setUploadingFiles(false);
                                  e.target.value = '';
                                }
                              }}
                              className="file-upload-input"
                            />
                          </label>
                          
                          {/* Preview uploaded files */}
                          {uploadingFiles && <p>Uploading files...</p>}
                          {uploadedFiles.length > 0 && (
                            <div className="uploaded-files">
                              <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Uploaded Files:</p>
                              <div className="attachment-grid">
                                {uploadedFiles.map((file, index) => (
                                  <div key={index} className="attachment-item">
                                    {file.mimetype?.startsWith('image/') ? (
                                      <>
                                        <img
                          src={file.path.startsWith('http') ? file.path : `${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                          alt={file.originalname || file.filename}
                                          className="attachment-image"
                                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                        />
                                        <span className="attachment-name">{file.originalname || file.filename}</span>
                                      </>
                                    ) : file.mimetype?.startsWith('video/') ? (
                                      <>
                                        <video
                                          src={`${API_URL}/api/collaborations/feedback-attachment/${file.filename}`}
                                          controls
                                          className="attachment-video"
                                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                        <span className="attachment-name">{file.originalname || file.filename}</span>
                                      </>
                                    ) : (
                                      <>
                                        <div className="attachment-link">📎 {file.originalname || file.filename}</div>
                                      </>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                                      className="file-remove-btn"
                                      style={{ marginTop: '5px' }}
                                    >
                                      ✕ Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button 
                          className="btn-additional-feedback"
                          onClick={() => handleReject('video')}
                          disabled={submitting || !feedback.trim()}
                        >
                          💬 Send Additional Feedback
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="status-box waiting">
                      <span>⏳</span> Waiting for video submission
                      <p className="status-description">The creator will upload their video soon.</p>
                    </div>
                  );
                }
              }
              
              // If video is approved (stage moved beyond video phases)
              if (['awaiting_post', 'reviewing_post', 'awaiting_payment_info', 'processing_payment', 'completed'].includes(collaboration.collaborationStage)) {
                return (
                  <div className="status-box approved">
                    <span>✅</span> Video Approved
                  </div>
                );
              }
              
              // If we're still in script phase
              if (['awaiting_script', 'reviewing_script'].includes(collaboration.collaborationStage)) {
                return (
                  <div className="status-box pending">
                    <span>⏸️</span> Script phase in progress
                    <p className="status-description">Video submission will be available after script approval.</p>
                  </div>
                );
              }
              
              return null;
            })()}
          </div>
        )}

        {activeTab === 'post' && (
          <div className="post-section">
            <h2>Post Information</h2>
            {(() => {
              // Check if any post URL exists
              const hasPostUrl = collaboration.postUrlInstagram || collaboration.postUrlTikTok || collaboration.postUrlYoutube;
              
              if (hasPostUrl) {
                return (
                  <div className="post-info">
                    {/* Post Timeline - Show postHistory if available */}
                    {collaboration.postHistory && collaboration.postHistory.length > 0 && (
                      <div className="admin-timeline">
                        <h3>📱 Post Timeline</h3>
                        {collaboration.postHistory
                          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                          .map((item, index) => (
                            <div key={index} className="timeline-row">
                              <div className="timeline-icon">
                                {item.type === 'submission' ? '📱' : '💬'}
                              </div>
                              <div className="timeline-content">
                                {item.type === 'submission' ? (
                                  <>
                                    <div className="timeline-header">
                                      <span className="timeline-label">Post URLs v{item.version}</span>
                                      <span className="timeline-time">{formatDate(item.timestamp)}</span>
                                    </div>
                                    <div className="timeline-text">
                                      {item.content.split('\n').map((line, idx) => 
                                        line.trim() ? (
                                          <div key={idx} style={{ marginBottom: '5px' }}>
                                            {line.includes('http') ? (
                                              <>
                                                {line.split(': ')[0]}: <a href={line.split(': ')[1]} target="_blank" rel="noopener noreferrer" className="post-link">🔗 {line.split(': ')[1]}</a>
                                              </>
                                            ) : line}
                                          </div>
                                        ) : null
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="timeline-header">
                                      <span className={`timeline-label ${item.author === 'Admin' ? 'admin-feedback' : item.author === 'Creator' ? 'creator-feedback' : ''}`}>
                                        {item.author === 'Admin' ? '👨‍💼 Admin' : item.author === 'Creator' ? '🎨 Creator' : 'Feedback'}
                                      </span>
                                      <span className="timeline-time">{formatDate(item.timestamp)}</span>
                                      {item.approved && <span className="timeline-approved">✅ Approved</span>}
                                    </div>
                                    <div className="timeline-feedback-text">{linkifyText(item.content)}</div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Platform-specific URLs display (fallback for older data) */}
                    {(!collaboration.postHistory || collaboration.postHistory.length === 0) && (
                      <div className="post-urls">
                        <h3>Published Posts</h3>
                        {collaboration.postUrlInstagram && (
                          <div className="post-url-item">
                            <strong>Instagram:</strong> 
                            <a href={collaboration.postUrlInstagram} target="_blank" rel="noopener noreferrer" className="post-link">
                              🔗 {collaboration.postUrlInstagram}
                            </a>
                          </div>
                        )}
                        {collaboration.postUrlTikTok && (
                          <>
                            <div className="post-url-item">
                              <strong>TikTok:</strong>
                              <a href={collaboration.postUrlTikTok} target="_blank" rel="noopener noreferrer" className="post-link">
                                🔗 {collaboration.postUrlTikTok}
                              </a>
                            </div>
                            {collaboration.sparkAdsCode && (
                              <div className="post-url-item" style={{ marginTop: '10px' }}>
                                <strong>⚡ Spark Ads Code:</strong>
                                <span style={{
                                  fontFamily: 'monospace',
                                  fontSize: '16px',
                                  letterSpacing: '2px',
                                  backgroundColor: '#f0f0f0',
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  marginLeft: '10px',
                                  userSelect: 'all'
                                }}>
                                  {collaboration.sparkAdsCode}
                                </span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(collaboration.sparkAdsCode || '');
                                    alert('Spark Ads code copied to clipboard!');
                                  }}
                                  style={{
                                    marginLeft: '10px',
                                    padding: '5px 10px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            )}
                          </>
                        )}
                        {collaboration.postUrlYoutube && (
                          <div className="post-url-item">
                            <strong>YouTube:</strong> 
                            <a href={collaboration.postUrlYoutube} target="_blank" rel="noopener noreferrer" className="post-link">
                              🔗 {collaboration.postUrlYoutube}
                            </a>
                          </div>
                        )}
                        
                        <div className="submission-meta">
                          <span>📅 Submitted: {formatDate(collaboration.postSubmittedAt)}</span>
                          {collaboration.postVerified && (
                            <span className="status-approved">✅ Verified</span>
                          )}
                          {collaboration.collaborationStage === 'reviewing_post' && (
                            <span className="status-reviewing">🔍 Under Review</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Review Section - Show based on collaboration stage */}
                    {(() => {
                      const lastEvent = collaboration.postHistory && collaboration.postHistory.length > 0
                        ? collaboration.postHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                        : null;
                      
                      // Check current collaboration stage
                      if (collaboration.collaborationStage === 'reviewing_post') {
                        return (
                          <div className="review-section">
                            <h3>🗒️ Review & Feedback</h3>
                            <div className="review-actions">
                              <button 
                                className="btn-reject"
                                onClick={async () => {
                                  // Handle post rejection
                                  const feedback = prompt('Provide feedback for the creator:');
                                  if (feedback) {
                                    try {
                                      const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/post/approve`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                                        body: JSON.stringify({ approved: false, feedback })
                                      });
                                      
                                      if (response.ok) {
                                        window.location.reload();
                                      } else {
                                        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
                                        console.error('Server error:', errorData);
                                        alert(`Failed to submit feedback: ${errorData.error || 'Server error'}`);
                                      }
                                    } catch (error) {
                                      console.error('Error rejecting post:', error);
                                      alert('Network error: Failed to submit feedback. Please check your connection and try again.');
                                    }
                                  }
                                }}
                              >
                                ❌ Request Changes
                              </button>
                              <button 
                                className="btn-approve"
                                onClick={async () => {
                                  if (window.confirm('Approve this post?')) {
                                    try {
                                      const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/post/approve`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                                        body: JSON.stringify({ approved: true })
                                      });
                                      
                                      if (response.ok) {
                                        window.location.reload();
                                      } else {
                                        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
                                        console.error('Server error:', errorData);
                                        alert(`Failed to approve post: ${errorData.error || 'Server error'}`);
                                      }
                                    } catch (error) {
                                      console.error('Error approving post:', error);
                                      alert('Network error: Failed to approve post. Please check your connection and try again.');
                                    }
                                  }
                                }}
                              >
                                ✅ Approve Post
                              </button>
                            </div>
                          </div>
                        );
                      }
                      
                      if (collaboration.collaborationStage === 'awaiting_post') {
                        if (lastEvent && lastEvent.type === 'feedback' && !lastEvent.approved) {
                          return (
                            <div className="status-box waiting">
                              <span>⏳</span> Waiting for revised post submission
                              <p className="status-description">Feedback has been sent to the creator. They will submit a revised post soon.</p>
                            </div>
                          );
                        } else {
                          return (
                            <div className="status-box waiting">
                              <span>⏳</span> Waiting for post submission
                              <p className="status-description">The creator will submit their post URL soon.</p>
                            </div>
                          );
                        }
                      }
                      
                      // If post is approved (stage moved beyond post phases)
                      if (['awaiting_payment_info', 'processing_payment', 'completed'].includes(collaboration.collaborationStage)) {
                        return (
                          <div className="status-box approved">
                            <span>✅</span> Post Approved
                          </div>
                        );
                      }
                      
                      return null;
                    })()}

                    {/* Validation information display */}
                    {collaboration.postValidation && (
                      <div className="validation-info">
                        <h3>🔍 Validation Results</h3>
                        <div className="validation-details">
                          <div className="validation-item">
                            <strong>Similarity Score:</strong> 
                            <span className={collaboration.postValidation.similarityScore >= 0.7 ? 'score-high' : 'score-low'}>
                              {`${(collaboration.postValidation.similarityScore * 100).toFixed(1)}%`}
                            </span>
                          </div>
                          <div className="validation-item">
                            <strong>Auto-Approved:</strong> 
                            <span>{collaboration.postValidation.isValid ? '✅ Yes' : '❌ No'}</span>
                          </div>
                          <div className="validation-item">
                            <strong>Analysis Method:</strong> 
                            <span>{collaboration.postValidation.analysisMethod || 'N/A'}</span>
                          </div>
                          <div className="validation-item">
                            <strong>Analysis Time:</strong> 
                            <span>{formatDate(collaboration.postValidation.timestamp)}</span>
                          </div>
                          {collaboration.postValidation.analysisDetails && (
                            <div className="validation-item">
                              <strong>Analysis Details:</strong>
                              <p className="analysis-details">{collaboration.postValidation.analysisDetails}</p>
                            </div>
                          )}
                          {collaboration.postValidation.transcript && (
                            <div className="validation-item">
                              <strong>Extracted Transcript:</strong>
                              <div className="transcript-box">
                                {collaboration.postValidation.transcript}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div className="empty-state">
                    <p>Post not yet submitted</p>
                  </div>
                );
              }
            })()}
          </div>
        )}

        {activeTab === 'payment' && (
          <div className="payment-section">
            <h2>Payment Information</h2>
            
            {/* Collaboration Stage Display */}
            <div className="payment-status-section">
              <h3>Collaboration Stage</h3>
              <div className="collaboration-stage-display">
                <div className="current-stage">
                  <span 
                    className="stage-badge" 
                    style={{ 
                      background: getStageColor(collaboration.collaborationStage),
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getStageDisplay(collaboration.collaborationStage)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Information Section */}
            {(collaboration.personalInfo || collaboration.invoiceLink) && (
              <div className="payment-info-section">
                <h3>Payment Information</h3>
                <div className="payment-info-display">
                  <div className="info-grid">
                    {collaboration.personalInfo && (
                      <>
                        <div className="info-item">
                          <strong>Name:</strong> {collaboration.personalInfo.firstName} {collaboration.personalInfo.lastName}
                        </div>
                        <div className="info-item">
                          <strong>Country:</strong> {collaboration.personalInfo.country}
                        </div>
                        <div className="info-item">
                          <strong>Address:</strong> {collaboration.personalInfo.address}
                        </div>
                        <div className="info-item">
                          <strong>City:</strong> {collaboration.personalInfo.city}
                        </div>
                        <div className="info-item">
                          <strong>State/Province:</strong> {collaboration.personalInfo.stateProvince}
                        </div>
                        <div className="info-item">
                          <strong>Postal Code:</strong> {collaboration.personalInfo.postalCode}
                        </div>
                      </>
                    )}
                    
                    {collaboration.invoiceLink && (
                      <div className="info-item">
                        <strong>PayPal Invoice:</strong> 
                        <a href={collaboration.invoiceLink} target="_blank" rel="noopener noreferrer" className="invoice-link-button">
                          🔗 View Invoice
                        </a>
                      </div>
                    )}
                    
                    <div className="info-item">
                      <strong>Payment Method:</strong> PayPal Invoice
                    </div>
                    
                    {collaboration.personalInfo?.submittedAt && (
                      <div className="info-item">
                        <strong>Submitted:</strong> {formatDate(collaboration.personalInfo.submittedAt)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Manual Payment Processing Instructions */}
            {collaboration.collaborationStage === 'processing_payment' && collaboration.invoiceLink && (
              <div className="manual-payment-section">
                <h3>💳 Manual Payment Processing Required</h3>
                <div className="payment-process-box">
                  <p className="process-intro">
                    The creator has submitted their PayPal invoice. Please process the payment manually:
                  </p>
                  <div className="payment-steps">
                    <ol>
                      <li>Click the PayPal invoice link above to open it in PayPal</li>
                      <li>Review the invoice details and amount (${collaboration.agreedPrice} USD)</li>
                      <li>Process the payment through PayPal</li>
                      <li>Once payment is completed, click "Mark as Paid" below</li>
                    </ol>
                  </div>
                  <div className="invoice-link-highlight">
                    <strong>Invoice Link:</strong>
                    <a href={collaboration.invoiceLink} target="_blank" rel="noopener noreferrer" className="invoice-link-btn">
                      🔗 Open PayPal Invoice
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Actions */}
            {collaboration.collaborationStage === 'processing_payment' && collaboration.personalInfo && collaboration.invoiceLink && (
              <div className="payment-actions-section">
                <h3>Payment Actions</h3>
                <div className="payment-actions">
                  <button
                    onClick={markAsPaid}
                    disabled={processingPayment}
                    className="action-btn mark-paid-btn"
                  >
                    {processingPayment ? (
                      <>⏳ Processing...</>
                    ) : (
                      <>✅ Mark as Paid</>
                    )}
                  </button>
                </div>
                
                {/* Error Display */}
                {paymentError && (
                  <div className="payment-error-display">
                    <h4>❌ Payment Error</h4>
                    <div className="error-content">
                      <div className="error-message">
                        <strong>Error:</strong> {paymentError}
                      </div>
                      {errorDetails && (
                        <div className="error-details">
                          {errorDetails.details && (
                            <div className="error-item">
                              <strong>Details:</strong> {errorDetails.details}
                            </div>
                          )}
                          {errorDetails.suggestion && (
                            <div className="error-item suggestion">
                              <strong>💡 Suggestion:</strong> {errorDetails.suggestion}
                            </div>
                          )}
                          <div className="error-item">
                            <strong>Time:</strong> {formatDate(errorDetails.timestamp)}
                          </div>
                          {errorDetails.retryable && (
                            <div className="error-item retry-hint">
                              <strong>🔄 This error may be retryable.</strong> Try using the "Retry Payment" button.
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setPaymentError(null);
                          setErrorDetails(null);
                        }}
                        className="dismiss-error-btn"
                      >
                        ✕ Dismiss
                      </button>
                    </div>
                  </div>
                )}
                
              </div>
            )}

            {/* Payment Details */}
            {collaboration.collaborationStage === 'completed' ? (
              <div className="payment-info">
                <div className="payment-success">
                  <h3>✅ Payment Completed</h3>
                  <div className="payment-details">
                    <p><strong>Amount:</strong> ${collaboration.paymentAmount || collaboration.agreedPrice}</p>
                    <p><strong>Date:</strong> {formatDate(collaboration.paymentDate)}</p>
                  </div>
                </div>
              </div>
            ) : collaboration.collaborationStage === 'processing_payment' ? (
              <div className="payment-info">
                <div className="payment-processing">
                  <h3>💳 Ready for Payment Processing</h3>
                  <p><strong>Invoice Amount:</strong> ${collaboration.agreedPrice}</p>
                  <p className="processing-note">PayPal invoice has been submitted. Please review and process the payment manually.</p>
                </div>
              </div>
            ) : (
              <div className="payment-info">
                <div className="payment-pending">
                  <p><strong>Agreed Amount:</strong> ${collaboration.agreedPrice}</p>
                  <p className="pending-note">Payment will be processed after collaboration completion</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'emails' && (
          <div className="email-history-section">
            <h2>Email History</h2>
            {emailHistory.length > 0 ? (
              <div className="email-history-list">
                {emailHistory.map((email) => {
                  // Check if this is a deadline reminder
                  const isDeadlineReminder = email.campaignId?.includes('deadline-reminder');
                  const daysOverdue = email.ruleName?.match(/(\d+) days? overdue/)?.[1];

                  return (
                    <div key={email._id} className="email-history-item">
                      <div className="email-header">
                        <h3>
                          {isDeadlineReminder && '⏰ '}
                          {email.subject || 'No Subject'}
                        </h3>
                        <span className="email-date">{formatDate(email.dateSent)}</span>
                      </div>
                      <div className="email-details">
                        {email.ruleName && (
                          <div className="email-rule">
                            <strong>Type:</strong> {' '}
                            {isDeadlineReminder ? (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                borderRadius: '3px',
                                fontSize: '12px'
                              }}>
                                DEADLINE REMINDER
                              </span>
                            ) : (
                              email.ruleName
                            )}
                            {daysOverdue && (
                              <span style={{ marginLeft: '10px', color: '#dc3545' }}>
                                ({daysOverdue} day{daysOverdue !== '1' ? 's' : ''} overdue)
                              </span>
                            )}
                          </div>
                        )}
                        <div className="email-status">
                          <strong>Status:</strong>
                          <span className={`status-badge status-${email.status}`}>
                            {email.status}
                          </span>
                        </div>
                        {email.campaignId && !isDeadlineReminder && (
                          <div className="email-campaign">
                            <strong>Campaign:</strong> {email.campaignId}
                          </div>
                        )}
                      </div>
                      {email.content && (
                        <details className="email-content-details">
                          <summary>View Email Content</summary>
                          <div
                            className="email-content"
                            dangerouslySetInnerHTML={{ __html: email.content }}
                          />
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <p>No email history available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat Widget */}
      <ChatWidget
        collaborationId={collaboration._id}
        userType="admin"
      />

      {/* Memo Modal */}
      {showMemoModal && (
        <div
          className="memo-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setShowMemoModal(false);
            setMemoContent('');
            setEditingMemoId(null);
          }}
        >
          <div
            className="memo-modal-content"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>📝 Memos</h2>
              <button
                onClick={() => {
                  setShowMemoModal(false);
                  setMemoContent('');
                  setEditingMemoId(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Existing Memos */}
            {collaboration.memos && collaboration.memos.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Saved Memos</h3>
                {collaboration.memos
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((memo) => (
                    <div
                      key={memo._id}
                      style={{
                        backgroundColor: '#f9fafb',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ whiteSpace: 'pre-wrap', marginBottom: '8px' }}>
                        {memo.content}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <small style={{ color: '#6b7280' }}>
                          {formatDate(memo.createdAt)}
                          {memo.updatedAt !== memo.createdAt && ' (edited)'}
                        </small>
                        <div>
                          <button
                            onClick={() => handleEditMemo(memo)}
                            style={{
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              marginRight: '4px'
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteMemo(memo._id)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Add/Edit Memo Form */}
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>
                {editingMemoId ? 'Edit Memo' : 'Add New Memo'}
              </h3>
              <textarea
                value={memoContent}
                onChange={(e) => setMemoContent(e.target.value)}
                placeholder="메모 내용을 입력하세요..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', gap: '8px' }}>
                {editingMemoId && (
                  <button
                    onClick={() => {
                      setMemoContent('');
                      setEditingMemoId(null);
                    }}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleAddMemo}
                  disabled={submittingMemo || !memoContent.trim()}
                  style={{
                    background: submittingMemo || !memoContent.trim() ? '#d1d5db' : '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: submittingMemo || !memoContent.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submittingMemo ? 'Saving...' : (editingMemoId ? 'Update' : 'Add Memo')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationDetail;